import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { auth } from '../../firebase/config';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './RoasDashboard.css';

const PERIODS = [
  { key: '7d', label: '1주' },
  { key: '30d', label: '1달' },
  { key: '365d', label: '1년' },
  { key: 'all', label: '전체' },
  { key: 'custom', label: '직접 지정' },
];

const fmt = (n) => (n || 0).toLocaleString('ko-KR');
const compactWon = (n) => `${Math.round((n || 0) / 10000).toLocaleString('ko-KR')}만`;
const getReportKey = (period, startDate, endDate) => (
  period === 'custom' ? `${period}:${startDate || ''}:${endDate || ''}` : period
);

// ── EV순 테이블 ──

const FunnelTable = React.memo(({ rows, fieldName, fieldLabel }) => {
  if (!rows || rows.length === 0) return <div className="roas-empty">데이터 없음</div>;
  const maxEV = Math.max(...rows.map(r => r.expected_value || 0), 1);
  const hasQty = rows.some(r => (r.pre_register_qty || 0) > (r.pre_registers || 0));
  const hasBreakdown = rows.some(r => (
    (r.renewal_revenue || 0) || (r.bulk_revenue || 0) || (r.unattributed_revenue || 0)
  ));
  return (
    <table className="roas-table">
      <thead>
        <tr>
          <th>{fieldLabel}</th>
          <th className="num">사전예약</th>
          {hasQty && <th className="num">예상 수량</th>}
          <th className="num">가입</th>
          <th className="num">결제</th>
          <th className="num">확정 매출</th>
          {hasBreakdown && <th className="num">광고매출</th>}
          {hasBreakdown && <th className="num">재결제</th>}
          {hasBreakdown && <th className="num">단체매출</th>}
          {hasBreakdown && <th className="num">귀속불가</th>}
          <th className="num">기대 매출(EV)</th>
          <th style={{ width: '100px' }}>EV</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const qty = row.pre_register_qty || 0;
          const regs = row.pre_registers || 0;
          const ev = row.expected_value || 0;
          const isB2B = qty > regs;
          return (
            <tr key={i}>
              <td className="name-cell">{row[fieldName] || '(없음)'}</td>
              <td className="num">{fmt(regs)}</td>
              {hasQty && <td className={`num${isB2B ? ' roas-b2b-highlight' : ''}`}>{fmt(qty)}</td>}
              <td className="num">{fmt(row.signups)}</td>
              <td className="num">{fmt(row.payments)}</td>
              <td className="num">{fmt(row.revenue)}원</td>
              {hasBreakdown && <td className="num">{fmt(row.campaign_revenue)}원</td>}
              {hasBreakdown && <td className="num">{fmt(row.renewal_revenue)}원</td>}
              {hasBreakdown && <td className="num">{fmt(row.bulk_revenue)}원</td>}
              {hasBreakdown && <td className="num">{fmt(row.unattributed_revenue)}원</td>}
              <td className="num roas-ev-value">{fmt(ev)}원</td>
              <td>
                <div className="roas-funnel-bar">
                  <div className="roas-funnel-fill roas-ev-bar" style={{ width: `${(ev / maxEV) * 100}%` }} />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
});

const FunnelChart = React.memo(({ rows, fieldName }) => {
  const chartRows = useMemo(() => (
    (rows || [])
      .slice(0, 10)
      .map((row) => ({
        name: row[fieldName] || '(없음)',
        pre_registers: row.pre_registers || 0,
        signups: row.signups || 0,
        payments: row.payments || 0,
        revenue: row.revenue || 0,
        expected_value: row.expected_value || 0,
      }))
  ), [fieldName, rows]);

  if (!chartRows.length) return <div className="roas-empty">그래프 데이터 없음</div>;
  return (
    <div className="roas-chart">
      <ResponsiveContainer width="100%" height={330}>
        <BarChart data={chartRows} margin={{ top: 12, right: 16, left: 4, bottom: 56 }}>
          <CartesianGrid stroke="#F2F4F6" vertical={false} />
          <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fill: '#6B7280', fontSize: 12 }} />
          <YAxis yAxisId="count" tick={{ fill: '#6B7280', fontSize: 12 }} />
          <YAxis yAxisId="money" orientation="right" tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={compactWon} />
          <Tooltip formatter={(value, name) => {
            if (name === '확정 매출' || name === '기대 매출') return [`${fmt(value)}원`, name];
            return [fmt(value), name];
          }} />
          <Legend />
          <Bar yAxisId="count" dataKey="pre_registers" name="사전예약" fill="#93C5FD" radius={[6, 6, 0, 0]} />
          <Bar yAxisId="count" dataKey="signups" name="가입" fill="#22C55E" radius={[6, 6, 0, 0]} />
          <Bar yAxisId="count" dataKey="payments" name="결제" fill="#F59E0B" radius={[6, 6, 0, 0]} />
          <Bar yAxisId="money" dataKey="revenue" name="확정 매출" fill="#3182F6" radius={[6, 6, 0, 0]} />
          <Bar yAxisId="money" dataKey="expected_value" name="기대 매출" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});

const campaignStageValue = (row, stage) => (
  row?.stages?.[stage]?.unique_actors || 0
);

const CampaignFunnelPanel = React.memo(({
  data,
  campaignDraft,
  error,
  fetching,
  family,
  onCampaignApply,
  onCampaignDraftChange,
  onFamilyChange,
  onRetry,
}) => {
  if (fetching && !data) {
    return <div className="roas-loading"><div className="roas-spinner" /><p>캠페인 퍼널 집계 중...</p></div>;
  }
  if (error) {
    return <div className="roas-error"><p>{error}</p><button onClick={onRetry}>다시 불러오기</button></div>;
  }
  if (!data) return <div className="roas-empty">캠페인 퍼널을 불러오지 못했어요.</div>;

  const stageMap = Object.fromEntries(data.stages.map(stage => [stage.key, stage]));
  const chartRows = data.stages.map(stage => ({
    name: stage.label,
    users: stage.unique_actors,
  }));
  const displayedStages = [
    ['landing_viewed', '랜딩'],
    ['generation_started', '생성 시작'],
    ['generation_completed', '작품 완성'],
    ['share_clicked', '공유'],
    ['handoff_clicked', '수정 연결'],
    ['handoff_consumed', '수정 도착'],
    ['library_clicked', '라이브러리'],
    ['recipient_started', '수신자 시작'],
    ['cross_handoff_landed', '교차 생성 도착'],
    ['cta_clicked', 'CTA'],
    ['quote_clicked', '견적'],
    ['checkout_viewed', '결제 페이지'],
    ['quote_requested', '견적 요청'],
    ['crosslink_clicked', '교차 이동'],
    ['registration_completed', '가입 완료'],
    ['trial_started', 'PRO 체험'],
    ['artifact_completed', '추가 자료 완성'],
    ['payment_complete', '결제'],
  ].filter(([key]) => Boolean(stageMap[key]));

  return (
    <div className="roas-campaign-funnel">
      <div className="roas-campaign-toolbar">
        <div>
          <strong>{data.label} 캠페인 퍼널</strong>
          <span>
            {data.start_date} ~ {data.end_date} · {data.campaign} · 고유 사용자 기준
          </span>
        </div>
        <div className="roas-campaign-filter">
          <input
            aria-label="UTM 캠페인"
            maxLength={80}
            onChange={(event) => onCampaignDraftChange(event.target.value)}
            placeholder="utm_campaign"
            value={campaignDraft}
          />
          <button
            disabled={fetching}
            onClick={onCampaignApply}
            type="button"
          >
            캠페인 조회
          </button>
        </div>
        {(data.available_families || []).length > 1 && (
          <select
            aria-label="캠페인 선택"
            value={family}
            onChange={(event) => onFamilyChange(event.target.value)}
          >
            {data.available_families.map(item => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        )}
      </div>

      <div className="roas-summary-grid roas-campaign-summary">
        {[
          ['landing_viewed', '랜딩 방문'],
          ['generation_completed', '작품 완성'],
          ['cross_handoff_landed', '교차 생성 도착'],
          ['artifact_completed', '추가 자료 완성'],
          ['payment_complete', '결제 완료'],
        ].filter(([key]) => Boolean(stageMap[key])).map(([key, label]) => (
          <div className="roas-summary-card" key={key}>
            <div className="roas-summary-label">{label}</div>
            <div className="roas-summary-value">{fmt(stageMap[key]?.unique_actors)}</div>
            {key !== 'landing_viewed' && (
              <div className="roas-summary-sub">
                랜딩 대비 {stageMap[key]?.rate_from_landing || 0}%
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="roas-section">
        <div className="roas-section-title">전체 단계별 고유 사용자</div>
        <div className="roas-chart roas-campaign-chart">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartRows} margin={{ top: 12, right: 12, left: 0, bottom: 72 }}>
              <CartesianGrid stroke="#F2F4F6" vertical={false} />
              <XAxis dataKey="name" angle={-32} textAnchor="end" interval={0} tick={{ fill: '#6B7280', fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
              <Tooltip formatter={(value) => [`${fmt(value)}명`, '고유 사용자']} />
              <Bar dataKey="users" name="고유 사용자" fill="#3182F6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="roas-section">
        <div className="roas-section-title">광고 소재별 퍼널 (utm_content)</div>
        <div className="roas-table-scroll">
          <table className="roas-table roas-campaign-table">
            <thead>
              <tr>
                <th>광고 소재</th>
                {displayedStages.map(([, label]) => <th className="num" key={label}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {(data.by_content || []).map(row => (
                <tr key={row.utm_content}>
                  <td className="name-cell">{row.utm_content}</td>
                  {displayedStages.map(([key]) => (
                    <td className="num" key={key}>{fmt(campaignStageValue(row, key))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(data.by_content || []).length && (
          <div className="roas-empty">이 기간에는 캠페인 이벤트가 없어요.</div>
        )}
      </div>
      <div className="roas-funnel-note">
        이 탭의 가입 완료는 <code>analytics_events.signup_complete</code> 고유 사용자입니다.
        전체 EV 탭 가입은 <code>users</code> 가입일입니다. 두 숫자는 출처가 달라 다를 수 있습니다.
        단체결제는 아래 전체 EV 탭·엑셀의 단체매출 칸에서 확인합니다.
      </div>
    </div>
  );
});

// ── B2B 파이프라인 ──

const B2BPipeline = React.memo(({ preRegs, evPerQty = 21000 }) => {
  const { orgRows, b2bRows, totalQty, pipelineEV } = useMemo(() => {
    const orgMap = {};
    (preRegs || []).forEach(pr => {
      const org = pr.organization || '(미입력)';
      if (!orgMap[org]) orgMap[org] = { count: 0, qty: 0, emails: new Set() };
      orgMap[org].count += 1;
      orgMap[org].qty += (pr.quantity || 1);
      orgMap[org].emails.add(pr.email);
    });
    const rows = Object.entries(orgMap)
      .map(([org, v]) => ({ org, ...v, uniqueEmails: v.emails.size }))
      .sort((a, b) => b.qty - a.qty);
    const b2b = rows.filter(r => r.qty > 1);
    return {
      orgRows: rows, b2bRows: b2b,
      totalQty: rows.reduce((s, r) => s + r.qty, 0),
      pipelineEV: b2b.reduce((s, r) => s + r.qty * evPerQty, 0),
    };
  }, [evPerQty, preRegs]);

  return (
    <>
      <div className="roas-summary-grid" style={{ marginBottom: '16px' }}>
        <div className="roas-summary-card">
          <div className="roas-summary-label">B2B 리드 (qty &gt; 1)</div>
          <div className="roas-summary-value">{b2bRows.length}개 기관</div>
        </div>
        <div className="roas-summary-card">
          <div className="roas-summary-label">B2B 예상 수량 합산</div>
          <div className="roas-summary-value">{fmt(b2bRows.reduce((s, r) => s + r.qty, 0))}건</div>
        </div>
        <div className="roas-summary-card">
          <div className="roas-summary-label">파이프라인 EV</div>
          <div className="roas-summary-value revenue">{fmt(pipelineEV)}원</div>
        </div>
        <div className="roas-summary-card">
          <div className="roas-summary-label">전체 예상 수량</div>
          <div className="roas-summary-value">{fmt(totalQty)}건</div>
        </div>
      </div>
      <div className="roas-section">
        <div className="roas-section-title">기관별 사전예약 현황 (예상 수량 내림차순)</div>
        {orgRows.length > 0 ? (
          <div className="roas-table-scroll">
            <table className="roas-table">
              <thead>
                <tr>
                  <th>기관/학교명</th>
                  <th className="num">신청 건수</th>
                  <th className="num">예상 수량 합</th>
                  <th className="num">신청자 수</th>
                  <th>등급</th>
                  <th className="num">파이프라인 EV</th>
                </tr>
              </thead>
              <tbody>
                {orgRows.map((row, i) => {
                  let grade = 'C';
                  if (row.qty >= 10) grade = 'S';
                  else if (row.qty >= 5) grade = 'A';
                  else if (row.qty >= 2) grade = 'B';
                  return (
                    <tr key={i} className={row.qty > 1 ? 'roas-row-b2b' : ''}>
                      <td className="name-cell">{row.org}</td>
                      <td className="num">{row.count}</td>
                      <td className={`num${row.qty > 1 ? ' roas-b2b-highlight' : ''}`}>{row.qty}</td>
                      <td className="num">{row.uniqueEmails}</td>
                      <td><span className={`roas-grade roas-grade-${grade.toLowerCase()}`}>{grade}</span></td>
                      <td className="num revenue">{fmt(row.qty * evPerQty)}원</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <div className="roas-empty">사전예약 데이터 없음</div>}
      </div>
    </>
  );
});

// ── 원본 데이터 테이블 ──

const RawSection = React.memo(({ label, count, expanded, onToggle, children }) => (
  <div className="roas-section">
    <button className="roas-raw-toggle" onClick={onToggle}>
      {expanded ? `${label} 숨기기` : `${label} (${count || 0}건)`}
    </button>
    {expanded && children}
  </div>
));

const PreRegTable = React.memo(({ items }) => {
  if (!items?.length) return <div className="roas-empty">데이터 없음</div>;
  const dupCount = items.filter(i => i.is_duplicate).length;
  const b2bCount = items.filter(i => (i.quantity || 1) > 1).length;
  return (
    <>
      {dupCount > 0 && <div className="roas-dup-notice">중복 신청 {dupCount}건 포함</div>}
      {b2bCount > 0 && <div className="roas-b2b-notice">단체 구매 리드 {b2bCount}건 (qty &gt; 1)</div>}
      <div className="roas-table-scroll">
        <table className="roas-table">
          <thead><tr><th>이메일</th><th>소속</th><th>상품</th><th className="num">수량</th><th>utm_source</th><th>utm_content</th><th>일시</th><th>중복</th></tr></thead>
          <tbody>
            {items.map((item, i) => {
              const qty = item.quantity || 1;
              return (
                <tr key={i} className={`${item.is_duplicate ? 'roas-row-dup' : ''}${qty > 1 ? ' roas-row-b2b' : ''}`}>
                  <td>{item.email}</td><td>{item.organization}</td><td>{item.selected_plan}</td>
                  <td className={`num${qty > 1 ? ' roas-b2b-highlight' : ''}`}>{qty}</td>
                  <td>{item.utm_source || '-'}</td><td>{item.utm_content || '-'}</td>
                  <td>{item.created_at ? item.created_at.substring(0, 16) : '-'}</td>
                  <td>{item.is_duplicate ? '중복' : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
});

const SignupTable = React.memo(({ items }) => {
  if (!items?.length) return <div className="roas-empty">데이터 없음</div>;
  return (
    <div className="roas-table-scroll">
      <table className="roas-table">
        <thead><tr><th>이메일</th><th>가입일</th><th>집계소스</th><th>집계캠페인</th><th>원본유입</th><th>원본세트</th><th>귀속</th><th>근거</th><th>쿠폰</th><th>구독</th></tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td>{item.email || '-'}</td><td>{item.created_at ? item.created_at.substring(0, 10) : '-'}</td>
              <td>{item.utm_source || '-'}</td><td>{item.utm_campaign || '-'}</td>
              <td>{item.raw_utm_source || '-'}</td><td>{item.raw_utm_content || '-'}</td>
              <td>{item.attribution_class || '-'}</td>
              <td>{item.attribution_reason || '-'}</td>
              <td>{item.current_coupon || '-'}</td>
              <td><span className={`roas-sub-badge ${(item.subscription_type || '').includes('pro') ? 'pro' : ''}`}>{item.subscription_type || 'free'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

const BulkTable = React.memo(({ items, emptyLabel }) => {
  if (!items?.length) return <div className="roas-empty">{emptyLabel || '데이터 없음'}</div>;
  return (
    <div className="roas-table-scroll">
      <table className="roas-table">
        <thead><tr><th>주문</th><th>상태</th><th className="num">금액</th><th className="num">인원</th><th>캠페인</th><th>광고세트</th><th>프로모</th><th>생성</th><th>결제</th></tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.order_id || i}>
              <td>{item.order_id || '-'}</td>
              <td>{item.status || '-'}</td>
              <td className="num revenue">{fmt(item.amount)}원</td>
              <td className="num">{item.quantity || 0}</td>
              <td>{item.utm_campaign || '-'}</td>
              <td>{item.utm_content || '-'}</td>
              <td>{item.promo_code || '-'}</td>
              <td>{item.created_at || '-'}</td>
              <td>{item.paid_at || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

const PaymentTable = React.memo(({ items }) => {
  if (!items?.length) return <div className="roas-empty">데이터 없음</div>;
  return (
    <div className="roas-table-scroll">
      <table className="roas-table">
        <thead><tr><th>이메일</th><th className="num">금액</th><th>플랜</th><th>가입일</th><th className="num">전환소요</th><th>귀속</th><th>매출구분</th><th>근거</th><th>최초유입</th><th>쿠폰</th><th>결제일</th></tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td>{item.user_email || '-'}</td><td className="num revenue">{fmt(item.amount)}원</td>
              <td>{item.plan_id}</td><td>{item.user_created_at ? item.user_created_at.substring(0, 10) : '-'}</td>
              <td className="num">{item.days_to_payment != null ? `${item.days_to_payment}일` : '-'}</td>
              <td>{item.attribution_class || item.attribution_method || '-'}</td>
              <td>{item.revenue_class || '-'}</td>
              <td>{item.attribution_reason || '-'}</td>
              <td>{item.raw_utm_source || item.first_utm_source || item.utm_source || '-'}</td>
              <td>{item.current_coupon || '-'}</td>
              <td>{item.created_at ? item.created_at.substring(0, 16) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

// ── 메인 대시보드 ──

const RoasDashboard = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchingRaw, setFetchingRaw] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [campaignError, setCampaignError] = useState(null);
  const [fetchingCampaign, setFetchingCampaign] = useState(false);
  const [campaignFamily, setCampaignFamily] = useState('classroom_bundle');
  const [campaignName, setCampaignName] = useState('class_photo_2026_q3');
  const [campaignDraft, setCampaignDraft] = useState('class_photo_2026_q3');
  const [activeTab, setActiveTab] = useState('all');
  const [expandedRaw, setExpandedRaw] = useState({});
  const [periodDraft, setPeriodDraft] = useState('7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const rawLoadedPeriodRef = useRef(null);
  const initialReportLoadedRef = useRef(false);

  const fetchReport = useCallback(async (p, range = {}) => {
    if (!user) return;
    setFetching(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({ period: p });
      if (p === 'custom') {
        if (range.startDate) params.set('start_date', range.startDate);
        if (range.endDate) params.set('end_date', range.endDate);
      }
      const res = await fetch(`${getApiBaseUrl()}/api/analytics/roas-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || '데이터 로딩 실패');
      }
      setData(await res.json());
      setRawData(null);
      rawLoadedPeriodRef.current = null;
    } catch (e) {
      setError(e.message);
    } finally {
      setFetching(false);
    }
  }, [user]);

  const fetchRaw = useCallback(async () => {
    const reportKey = getReportKey(period, startDate, endDate);
    if (!user || rawLoadedPeriodRef.current === reportKey) return;
    setFetchingRaw(true);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({ period, include_raw: 'true' });
      if (period === 'custom') {
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
      }
      const res = await fetch(`${getApiBaseUrl()}/api/analytics/roas-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('원본 데이터 로딩 실패');
      const result = await res.json();
      setRawData(result.raw || null);
      rawLoadedPeriodRef.current = reportKey;
    } catch (e) {
      setError(e.message);
    } finally {
      setFetchingRaw(false);
    }
  }, [endDate, startDate, user, period]);

  const fetchCampaignFunnel = useCallback(async (
    p,
    range = {},
    family = campaignFamily,
    campaign = campaignName,
  ) => {
    if (!user) return;
    setFetchingCampaign(true);
    setCampaignError(null);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({ campaign, family, period: p });
      if (p === 'custom') {
        if (range.startDate) params.set('start_date', range.startDate);
        if (range.endDate) params.set('end_date', range.endDate);
      }
      const response = await fetch(
        `${getApiBaseUrl()}/api/analytics/campaign-funnel?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.detail || '캠페인 퍼널을 불러오지 못했어요.');
      }
      setCampaignData(result);
    } catch (campaignFetchError) {
      setCampaignError(campaignFetchError.message);
    } finally {
      setFetchingCampaign(false);
    }
  }, [campaignFamily, campaignName, user]);

  useEffect(() => {
    if (loading || !user || initialReportLoadedRef.current) return;
    initialReportLoadedRef.current = true;
    fetchReport(period);
  }, [user, loading, period, fetchReport]);

  const handlePeriodChange = useCallback((nextPeriod) => {
    setPeriodDraft(nextPeriod);

    if (nextPeriod === 'custom') {
      setDraftStartDate(startDate);
      setDraftEndDate(endDate);
      return;
    }

    setPeriod(nextPeriod);
    setStartDate('');
    setEndDate('');
    fetchReport(nextPeriod);
    if (activeTab === 'campaign' || campaignData) {
      fetchCampaignFunnel(nextPeriod);
    }
  }, [
    activeTab,
    campaignData,
    endDate,
    fetchCampaignFunnel,
    fetchReport,
    startDate,
  ]);

  const handleCustomDateApply = useCallback(() => {
    if (!draftStartDate || !draftEndDate) {
      setError('시작일과 종료일을 모두 선택해주세요.');
      return;
    }

    if (draftStartDate > draftEndDate) {
      setError('종료일은 시작일 이후로 선택해주세요.');
      return;
    }

    setPeriod('custom');
    setPeriodDraft('custom');
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    fetchReport('custom', { startDate: draftStartDate, endDate: draftEndDate });
    if (activeTab === 'campaign' || campaignData) {
      fetchCampaignFunnel('custom', {
        startDate: draftStartDate,
        endDate: draftEndDate,
      });
    }
  }, [
    activeTab,
    campaignData,
    draftEndDate,
    draftStartDate,
    fetchCampaignFunnel,
    fetchReport,
  ]);

  const handleTabChange = useCallback((tab) => {
    const reportKey = getReportKey(period, startDate, endDate);
    setActiveTab(tab);
    if (tab === 'raw' || tab === 'b2b') {
      if (!rawData && rawLoadedPeriodRef.current !== reportKey) {
        fetchRaw();
      }
    }
    if (tab === 'campaign' && !campaignData) {
      fetchCampaignFunnel(period, { startDate, endDate });
    }
  }, [
    campaignData,
    endDate,
    rawData,
    period,
    fetchCampaignFunnel,
    fetchRaw,
    startDate,
  ]);

  const handleCampaignFamilyChange = useCallback((nextFamily) => {
    const familyConfig = (campaignData?.available_families || [])
      .find((item) => item.key === nextFamily);
    const nextCampaign = familyConfig?.default_campaign || campaignName;
    setCampaignFamily(nextFamily);
    setCampaignName(nextCampaign);
    setCampaignDraft(nextCampaign);
    fetchCampaignFunnel(
      period,
      { startDate, endDate },
      nextFamily,
      nextCampaign,
    );
  }, [
    campaignData,
    campaignName,
    endDate,
    fetchCampaignFunnel,
    period,
    startDate,
  ]);

  const handleCampaignApply = useCallback(() => {
    const normalized = campaignDraft.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{0,79}$/.test(normalized)) {
      setCampaignError('utm_campaign 형식을 확인해주세요.');
      return;
    }
    setCampaignName(normalized);
    setCampaignDraft(normalized);
    fetchCampaignFunnel(
      period,
      { startDate, endDate },
      campaignFamily,
      normalized,
    );
  }, [
    campaignDraft,
    campaignFamily,
    endDate,
    fetchCampaignFunnel,
    period,
    startDate,
  ]);

  const retryFetchReport = useCallback(() => {
    fetchReport(period, { startDate, endDate });
  }, [endDate, fetchReport, period, startDate]);
  const retryCampaignFunnel = useCallback(() => {
    fetchCampaignFunnel(period, { startDate, endDate });
  }, [endDate, fetchCampaignFunnel, period, startDate]);

  const toggleRaw = useCallback((key) => setExpandedRaw(prev => ({ ...prev, [key]: !prev[key] })), []);

  const downloadExcel = useCallback(async () => {
    if (!data) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();

    if (data.by_content?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.by_content.map(r => ({
        '광고세트': r.utm_content || '',
        '사전예약': r.pre_registers || 0, '예상수량': r.pre_register_qty || 0,
        '가입': r.signups || 0, '결제': r.payments || 0,
        '확정매출': r.revenue || 0, '광고매출': r.campaign_revenue || 0,
        '재결제매출': r.renewal_revenue || 0, '단체매출': r.bulk_revenue || 0,
        '귀속불가매출': r.unattributed_revenue || 0, '기대매출(EV)': r.expected_value || 0,
      }))), '광고세트별');
    }
    if (data.by_campaign?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.by_campaign.map(r => ({
        '캠페인': r.utm_campaign || '',
        '사전예약': r.pre_registers || 0, '예상수량': r.pre_register_qty || 0,
        '가입': r.signups || 0, '결제': r.payments || 0,
        '확정매출': r.revenue || 0, '광고매출': r.campaign_revenue || 0,
        '재결제매출': r.renewal_revenue || 0, '단체매출': r.bulk_revenue || 0,
        '귀속불가매출': r.unattributed_revenue || 0, '기대매출(EV)': r.expected_value || 0,
      }))), '캠페인별');
    }
    if (data.by_source?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.by_source.map(r => ({
        '소스': r.utm_source || '', '사전예약': r.pre_registers || 0,
        '예상수량': r.pre_register_qty || 0, '가입': r.signups || 0,
        '결제': r.payments || 0, '확정매출': r.revenue || 0, '광고매출': r.campaign_revenue || 0,
        '재결제매출': r.renewal_revenue || 0, '단체매출': r.bulk_revenue || 0,
        '귀속불가매출': r.unattributed_revenue || 0, '기대매출(EV)': r.expected_value || 0,
      }))), '소스별');
    }

    if (rawData) {
      if (rawData.pre_registrations?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawData.pre_registrations.map(r => ({
          '이메일': r.email || '', '이름': r.name || '', '소속': r.organization || '',
          '상품': r.selected_plan || '', '구매예상수량': r.quantity || 1,
          'utm_source': r.utm_source || '', 'utm_campaign': r.utm_campaign || '',
          'utm_content': r.utm_content || '',
          '일시(KST)': r.created_at || '', '중복여부': r.is_duplicate ? '중복' : '',
        }))), '사전예약');
      }
      if (rawData.signups?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawData.signups.map(r => ({
          '이메일': r.email || '', '가입일(KST)': r.created_at || '',
          'utm_source': r.utm_source || '', 'utm_campaign': r.utm_campaign || '',
          'utm_content': r.utm_content || '',
          '원본utm_source': r.raw_utm_source || '', '원본utm_content': r.raw_utm_content || '',
          '귀속': r.attribution_class || '', '근거': r.attribution_reason || '',
          '쿠폰': r.current_coupon || '', '구독타입': r.subscription_type || 'free',
          '로그인수': r.login_count || 0,
        }))), '가입자');
      }
      if (rawData.payments?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawData.payments.map(r => ({
          '이메일': r.user_email || '', '금액': r.amount || 0, '플랜': r.plan_id || '',
          '가입일(KST)': r.user_created_at || '', '전환소요(일)': r.days_to_payment ?? '',
          'utm_source': r.utm_source || '', 'utm_content': r.utm_content || '',
          '원본utm_source': r.raw_utm_source || '', '귀속': r.attribution_class || '',
          '매출구분': r.revenue_class || '', '근거': r.attribution_reason || '',
          '최초유입': r.first_utm_source || '', '결제일(KST)': r.created_at || '',
        }))), '결제');
      }
      if (rawData.bulk_orders?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawData.bulk_orders.map(r => ({
          '주문': r.order_id || '', '상태': r.status || '', '금액': r.amount || 0,
          '인원': r.quantity || 0, 'utm_source': r.utm_source || '',
          'utm_campaign': r.utm_campaign || '', 'utm_content': r.utm_content || '',
          '프로모': r.promo_code || '', '생성(KST)': r.created_at || '', '결제(KST)': r.paid_at || '',
        }))), '단체결제');
      }
      if (rawData.bulk_pending?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rawData.bulk_pending.map(r => ({
          '주문': r.order_id || '', '상태': r.status || '', '금액': r.amount || 0,
          '인원': r.quantity || 0, 'utm_campaign': r.utm_campaign || '',
          'utm_content': r.utm_content || '', '생성(KST)': r.created_at || '',
        }))), '단체결제_미결제');
      }
    }

    XLSX.writeFile(wb, `ROAS_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [data, rawData, period]);

  if (loading) return <div className="roas-dashboard"><div className="roas-loading"><div className="roas-spinner" /><p>로딩 중...</p></div></div>;
  if (!user) { navigate('/login'); return null; }

  const s = data?.summary || {};
  const evRates = s.ev_rates;
  const isCustomDraftComplete = Boolean(draftStartDate && draftEndDate);

  return (
    <div className="roas-dashboard">
      <Helmet><title>ROAS 대시보드 | WhatQuiz Admin</title></Helmet>

      <div className="roas-header">
        <div>
          <h1>ROAS 대시보드</h1>
          <div className="roas-header-sub">
            production Firebase · 광고 세트별 EV 성과 · 사전예약 · 가입 · 결제 · 매출
            {data?.generated_at && <span className="roas-generated-at"> · 기준: {data.generated_at}</span>}
            {data?.snapshot && (
              <span className="roas-generated-at">
                {' '}· 워커 {data.snapshot.worker_pid} · users 캐시 {data.snapshot.users_cache_age_sec ?? '-'}s/{data.snapshot.users_cache_ttl_sec}s
              </span>
            )}
          </div>
        </div>
        <div className="roas-header-actions">
          <div className="roas-period-tabs">
            {PERIODS.map((p) => (
              <button key={p.key} className={`roas-period-tab ${periodDraft === p.key ? 'active' : ''}`} onClick={() => handlePeriodChange(p.key)} disabled={fetching}>{p.label}</button>
            ))}
          </div>
            {periodDraft === 'custom' && (
              <div className="roas-date-range">
                <input type="date" value={draftStartDate} onChange={(event) => setDraftStartDate(event.target.value)} aria-label="시작일" />
                <span className="roas-date-separator">~</span>
                <input type="date" value={draftEndDate} onChange={(event) => setDraftEndDate(event.target.value)} aria-label="종료일" />
                <button className="roas-date-apply-btn" type="button" onClick={handleCustomDateApply} disabled={fetching || !isCustomDraftComplete}>기간 적용</button>
              </div>
            )}
          {data && <button className="roas-excel-btn" onClick={downloadExcel} disabled={fetching}>엑셀 다운로드</button>}
        </div>
      </div>

      {error && <div className="roas-error"><p>{error}</p><button onClick={retryFetchReport}>다시 시도</button></div>}
      {fetching && !data && <div className="roas-loading"><div className="roas-spinner" /><p>분석 중...</p></div>}

      {data && (
        <>
          {fetching && <div className="roas-loading-overlay"><div className="roas-spinner" /><p>기간 변경 중...</p></div>}

          <div className="roas-summary-grid roas-summary-grid-wide">
            <div className="roas-summary-card">
              <div className="roas-summary-label">사전예약</div>
              <div className="roas-summary-value">{fmt(s.total_pre_registers)}</div>
              {(s.total_pre_register_qty || 0) > (s.total_pre_registers || 0) && (
                <div className="roas-summary-sub">예상 수량 {fmt(s.total_pre_register_qty)}건</div>
              )}
            </div>
            <div className="roas-summary-card">
              <div className="roas-summary-label">가입 (전체)</div>
              <div className="roas-summary-value">{fmt(s.total_signups)}</div>
              <div className="roas-summary-sub">
                UTM {fmt(s.total_signups_utm)} · 없음 {fmt(s.total_signups_no_utm)} · 귀속불가 {fmt(s.total_signups_unresolved)}
              </div>
            </div>
            <div className="roas-summary-card">
              <div className="roas-summary-label">개인 결제</div>
              <div className="roas-summary-value">{fmt(s.total_payments)}</div>
              <div className="roas-summary-sub">재결제 {fmt(s.total_renewal_payments)} · 귀속불가 {fmt(s.total_unattributed_payments)}</div>
            </div>
            <div className="roas-summary-card">
              <div className="roas-summary-label">개인 확정 매출</div>
              <div className="roas-summary-value revenue">{fmt(s.total_revenue)}원</div>
              <div className="roas-summary-sub">광고 {fmt(s.total_campaign_revenue)} · 재결제 {fmt(s.total_renewal_revenue)}</div>
            </div>
            <div className="roas-summary-card">
              <div className="roas-summary-label">단체 결제</div>
              <div className="roas-summary-value revenue">{fmt(s.total_bulk_revenue)}원</div>
              <div className="roas-summary-sub">
                {fmt(s.total_bulk_payments)}건 · 미결제 {fmt(s.total_bulk_pending)}건 {fmt(s.total_bulk_pending_amount)}원
              </div>
            </div>
            <div className="roas-summary-card roas-ev-card">
              <div className="roas-summary-label">기대 매출 (EV)</div>
              <div className="roas-summary-value roas-ev-value">{fmt(s.total_expected_value)}원</div>
              <div className="roas-summary-sub">
                개인+단체 + 사전예약(qty×{evRates?.pre_reg_per_qty || 21000}) + 가입×{evRates?.per_signup || 7000}
              </div>
            </div>
          </div>

          <div className="roas-tabs">
            {[
              { key: 'all', label: '전체 EV순' },
              { key: 'campaign', label: '캠페인 퍼널' },
              { key: 'b2b', label: 'B2B 파이프라인' },
              { key: 'raw', label: '원본 데이터' },
            ].map(t => (
              <button key={t.key} className={`roas-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => handleTabChange(t.key)}>{t.label}</button>
            ))}
          </div>

          {activeTab === 'all' && (
            <>
              <div className="roas-section">
                <div className="roas-section-title">광고 세트별 성과 — EV 내림차순 (utm_content)</div>
                <FunnelChart rows={data.by_content} fieldName="utm_content" />
                <FunnelTable rows={data.by_content} fieldName="utm_content" fieldLabel="광고 세트" />
              </div>
              <div className="roas-section">
                <div className="roas-section-title">캠페인별 성과 (utm_campaign)</div>
                <FunnelChart rows={data.by_campaign} fieldName="utm_campaign" />
                <FunnelTable rows={data.by_campaign} fieldName="utm_campaign" fieldLabel="캠페인" />
              </div>
              <div className="roas-section">
                <div className="roas-section-title">소스별 성과 (utm_source)</div>
                <FunnelChart rows={data.by_source} fieldName="utm_source" />
                <FunnelTable rows={data.by_source} fieldName="utm_source" fieldLabel="소스" />
              </div>
            </>
          )}

          {activeTab === 'campaign' && (
            <CampaignFunnelPanel
              campaignDraft={campaignDraft}
              data={campaignData}
              error={campaignError}
              family={campaignFamily}
              fetching={fetchingCampaign}
              onCampaignApply={handleCampaignApply}
              onCampaignDraftChange={setCampaignDraft}
              onFamilyChange={handleCampaignFamilyChange}
              onRetry={retryCampaignFunnel}
            />
          )}

          {activeTab === 'b2b' && (
            rawData ? (
              <B2BPipeline evPerQty={evRates?.pre_reg_per_qty || 21000} preRegs={rawData.pre_registrations} />
            ) : fetchingRaw ? (
              <div className="roas-loading"><div className="roas-spinner" /><p>B2B 데이터 로딩 중...</p></div>
            ) : (
              <div className="roas-loading"><div className="roas-spinner" /><p>데이터 로딩 중...</p></div>
            )
          )}

          {activeTab === 'raw' && (
            rawData ? (
              <div className="roas-raw-wrap">
                <RawSection label="사전예약" count={rawData.pre_registrations?.length} expanded={expandedRaw.preReg} onToggle={() => toggleRaw('preReg')}>
                  <PreRegTable items={rawData.pre_registrations} />
                </RawSection>
                <RawSection label="가입자" count={rawData.signups?.length} expanded={expandedRaw.signups} onToggle={() => toggleRaw('signups')}>
                  <SignupTable items={rawData.signups} />
                </RawSection>
                <RawSection label="결제" count={rawData.payments?.length} expanded={expandedRaw.payments} onToggle={() => toggleRaw('payments')}>
                  <PaymentTable items={rawData.payments} />
                </RawSection>
                <RawSection label="단체결제" count={rawData.bulk_orders?.length} expanded={expandedRaw.bulk} onToggle={() => toggleRaw('bulk')}>
                  <BulkTable items={rawData.bulk_orders} emptyLabel="단체결제 없음" />
                </RawSection>
                <RawSection label="단체결제 미결제" count={rawData.bulk_pending?.length} expanded={expandedRaw.bulkPending} onToggle={() => toggleRaw('bulkPending')}>
                  <BulkTable items={rawData.bulk_pending} emptyLabel="미결제 단체주문 없음" />
                </RawSection>
              </div>
            ) : fetchingRaw ? (
              <div className="roas-loading"><div className="roas-spinner" /><p>원본 데이터 로딩 중...</p></div>
            ) : (
              <div className="roas-loading"><div className="roas-spinner" /><p>데이터 로딩 중...</p></div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default RoasDashboard;
